import { useQueries } from "@tanstack/react-query";

import { erc20Abi, parseAbiItem, type Address } from "viem";
import { keelVaultAbi, keelVaultFactoryAbi } from "../abi";
import { redesActivas, type RedKeel } from "../config/networks";
import { clientePublico, clienteLogs, logsConVentanas } from "../lib/cliente";
import { cobradoPorVault, type Cobrado } from "../lib/ganado";

const OPENED = parseAbiItem("event Opened(uint256 costBasisRef, int24 openTick)");
// Los DOS eventos de cierre: `withdraw`/`withdrawTo` (manual) y `takeProfit` (por objetivo). Solo se
// usa su bloque, para saber CUÁNDO cerró y calcular cuánto vivió la piscina.
const WITHDRAWN = parseAbiItem("event Withdrawn(address indexed to, uint256 amount0, uint256 amount1)");
const TOOKPROFIT = parseAbiItem("event TookProfit(uint256 logrado, uint256 objetivo, uint256 capital)");

const POOL_ABI = [
  // slot0 truncado a (sqrtPriceX96, tick): Slipstream devuelve 6 campos, Uniswap 7; con 2 vale para ambos
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
] as const;

const MAX_U128 = 2n ** 128n - 1n;

const NPM_ABI = [
  { type: "function", name: "positions", stateMutability: "view", inputs: [{ type: "uint256" }],
    outputs: [{ type: "uint96" }, { type: "address" }, { type: "address" }, { type: "address" },
              { type: "uint24" }, { type: "int24" }, { type: "int24" }, { type: "uint128" },
              { type: "uint256" }, { type: "uint256" }, { type: "uint128" }, { type: "uint128" }] },
  { type: "function", name: "collect", stateMutability: "payable",
    inputs: [{ type: "tuple", components: [
      { name: "tokenId", type: "uint256" }, { name: "recipient", type: "address" },
      { name: "amount0Max", type: "uint128" }, { name: "amount1Max", type: "uint128" },
    ] }],
    outputs: [{ type: "uint256" }, { type: "uint256" }] },
] as const;

export type VaultPublico = {
  direccion: Address;
  red: RedKeel["clave"];
  /** el pool donde vive la posición: lo necesita el botón «Copiar» de la portada para abrir una
   *  piscina igual (mismo pool y rango) sin que el visitante tenga que buscarlo a mano. */
  pool: Address;
  par: string;
  /** los dos simbolos sueltos: sin ellos la portada no puede enseñar el precio al derecho */
  s0: string;
  s1: string;
  /** lo que la posición vale AHORA a precio de mercado. Es lo que se suma en el total de la portada. */
  valor: number;
  /**
   * El capital de trabajo: `costBasisRef`, lo aportado MÁS lo que se haya reinvertido —el contrato
   * lo suma ahí con `addCapital`—. Es la misma cifra que la lista privada llama «Capital», y por eso
   * se enseña esta y no `valor`: dos pantallas con la misma palabra tienen que dar el mismo número.
   */
  capital: number;
  simboloRef: string;
  precio: number;
  lo: number;
  hi: number;
  enRango: boolean;
  /** todo lo ganado DESDE QUE ABRIÓ, en la moneda de referencia. undefined = el vault no lo publica */
  ganado?: number;
  pctGanado?: number;
  /** cuando se abrio la posicion viva, en segundos */
  abierta?: number;
};

/** Una piscina YA CERRADA, para la pestaña «Cerradas»: par, red, cuánto ganó, con qué capital empezó
 *  y terminó, el % y cuánto vivió. Todo en la moneda de referencia (`simboloRef`). */
export type VaultCerrada = {
  direccion: Address;
  red: RedKeel["clave"];
  par: string;
  /** todo lo que ganó para su dueño mientras estuvo viva, en la moneda de referencia. */
  ganado: number;
  /** capital con el que abrió (`costBasisRef` del evento `Opened`). */
  capital: number;
  /** con cuánto salió = capital + lo ganado. */
  salio: number;
  /** % ganado sobre el capital. undefined si no se pudo leer el capital. */
  pct?: number;
  /** segundos que vivió (apertura → cierre). undefined si falta alguna fecha. */
  duro?: number;
  /** la moneda del capital/salió (referencia del vault). */
  simboloRef: string;
};

export type Flota = {
  vaults: VaultPublico[];
  total: number;
  enRango: number;
  redesCaidas: string[];
  /** lo que repartieron los vaults YA CERRADOS mientras vivieron. No son flota —sin posición viva
   *  no se pintan en «activas»—, pero su ganado es dinero real de los dueños y entra en «Ganancias
   *  generadas». El total y el desglose por red, para que el KPI respete el filtro como los demás. */
  ganadoCerrado: number;
  ganadoCerradoPorRed: Partial<Record<RedKeel["clave"], number>>;
  /** las cerradas una a una, para la pestaña «Cerradas» del dashboard. */
  cerradas: VaultCerrada[];
};

/**
 * LO QUE REPARTIERON LOS VAULTS YA CERRADOS. No entran en la flota —sin posición viva enseñarlos
 * infla el número y le quita valor al que trabaja—, pero lo que ganaron para sus dueños mientras
 * vivieron es dinero real, y la portada lo suma en «Ganancias generadas». Se leen aparte y SOLO los
 * que repartieron algo: un contrato cerrado y sin un solo cobro no cuesta una lectura.
 *
 * La cuenta es la MISMA que para los vivos —el mayor entre el histórico de `PerfFeeTaken` y lo que
 * quede apartado en `profit0/1`, token a token, pasado a la moneda de referencia— salvo que aquí no
 * hay pendiente: sin NFT no queda nada por cobrar dentro de la posición.
 */
async function ganadoDeCerrados(
  client: NonNullable<ReturnType<typeof clientePublico>>,
  logs: ReturnType<typeof clienteLogs>,
  red: RedKeel,
  cerrados: Address[],
  historico: Map<string, Cobrado>,
): Promise<{ total: number; lista: VaultCerrada[] }> {
  const conCobro = cerrados.filter((d) => {
    const c = historico.get(d.toLowerCase());
    return c && (c.bruto0 > 0n || c.bruto1 > 0n);
  });
  if (!conCobro.length) return { total: 0, lista: [] };

  // estado: el pool, sus dos tokens, cuál es la referencia y lo que quede apartado sin retirar.
  const EST = ["pool", "token0", "token1", "recoverToken0", "profit0", "profit1"] as const;
  const est = await client.multicall({
    allowFailure: true,
    contracts: conCobro.flatMap((d) =>
      EST.map((functionName) => ({ address: d, abi: keelVaultAbi, functionName }))
    ),
  });
  const dEst = <T,>(i: number, c: (typeof EST)[number]): T | undefined => {
    const r = est[i * EST.length + EST.indexOf(c)];
    return r?.status === "success" ? (r.result as T) : undefined;
  };
  const conEstado = conCobro
    .map((direccion, i) => ({
      direccion,
      pool: dEst<Address>(i, "pool"),
      t0: dEst<Address>(i, "token0"),
      t1: dEst<Address>(i, "token1"),
      refEsToken0: dEst<boolean>(i, "recoverToken0") ?? true,
      pr0: dEst<bigint>(i, "profit0"),
      pr1: dEst<bigint>(i, "profit1"),
    }))
    .filter((v) => v.pool && v.t0 && v.t1);
  if (!conEstado.length) return { total: 0, lista: [] };

  // mercado: símbolos (para el par de la fila) + decimales + precio del pool, para pasar el bruto a
  // la referencia. Se añaden los símbolos respecto a los vivos porque la pestaña «Cerradas» los pinta.
  const POR = 5;
  const mkt = await client.multicall({
    allowFailure: true,
    contracts: conEstado.flatMap((v) => [
      { address: v.t0!, abi: erc20Abi, functionName: "symbol" as const },
      { address: v.t0!, abi: erc20Abi, functionName: "decimals" as const },
      { address: v.t1!, abi: erc20Abi, functionName: "symbol" as const },
      { address: v.t1!, abi: erc20Abi, functionName: "decimals" as const },
      { address: v.pool!, abi: POOL_ABI, functionName: "slot0" as const },
    ]),
  });
  const dMkt = <T,>(i: number, j: number): T | undefined => {
    const r = mkt[i * POR + j];
    return r?.status === "success" ? (r.result as T) : undefined;
  };

  // ---- eventos de cada cerrada: el CAPITAL con el que abrió (del último `Opened`, porque el estado
  //      se resetea a 0 al cerrar) y CUÁNDO abrió/cerró, para el % y cuánto vivió.
  //      SECUENCIAL, no en paralelo: en Robinhood (bloques ~0,1 s → muchos logs por ventana) tres
  //      escaneos a la vez saturaban el RPC y los de cierre volvían vacíos —la duración salía «—».
  const dirs = conEstado.map((v) => v.direccion);
  const aperturas = await logsConVentanas<{ address: string; blockNumber: bigint; args: { costBasisRef: bigint } }>(
    logs, { address: dirs, event: OPENED }, red, { acumula: true }
  ).then((ls) => {
    const m = new Map<string, { block: bigint; capital: bigint }>();
    for (const l of ls) {
      const k = l.address.toLowerCase();
      if ((l.blockNumber ?? 0n) > (m.get(k)?.block ?? -1n))
        m.set(k, { block: l.blockNumber ?? 0n, capital: l.args?.costBasisRef ?? 0n });
    }
    return m;
  }).catch(() => new Map<string, { block: bigint; capital: bigint }>());

  const cierres = new Map<string, bigint>();
  const anotaCierre = (ls: { address: string; blockNumber: bigint }[]) => {
    for (const l of ls) {
      const k = l.address.toLowerCase();
      if ((l.blockNumber ?? 0n) > (cierres.get(k) ?? -1n)) cierres.set(k, l.blockNumber ?? 0n);
    }
  };
  anotaCierre(await logsConVentanas<{ address: string; blockNumber: bigint }>(logs, { address: dirs, event: WITHDRAWN }, red, { acumula: true }).catch(() => []));
  anotaCierre(await logsConVentanas<{ address: string; blockNumber: bigint }>(logs, { address: dirs, event: TOOKPROFIT }, red, { acumula: true }).catch(() => []));
  // fechas de los bloques de apertura y cierre
  const bloques = new Set<bigint>();
  for (const a of aperturas.values()) bloques.add(a.block);
  for (const b of cierres.values()) bloques.add(b);
  const fechas = new Map<bigint, number>();
  await Promise.all([...bloques].map((b) =>
    client.getBlock({ blockNumber: b }).then((x) => fechas.set(b, Number(x.timestamp))).catch(() => undefined)
  ));

  const mayor = (a: bigint, b: bigint | undefined) => (a > (b ?? 0n) ? a : b ?? 0n);
  let total = 0;
  const lista: VaultCerrada[] = [];
  for (const [i, v] of conEstado.entries()) {
    const s0 = dMkt<string>(i, 0), d0 = dMkt<number>(i, 1);
    const s1 = dMkt<string>(i, 2), d1 = dMkt<number>(i, 3);
    const slot0 = dMkt<readonly [bigint, number]>(i, 4);
    if (d0 === undefined || d1 === undefined || !slot0) continue;
    const sp = Number(slot0[0]) / 2 ** 96;
    const precio = sp * sp * 10 ** (d0 - d1);
    const c = historico.get(v.direccion.toLowerCase())!;
    const x0 = Number(mayor(c.bruto0, v.pr0)) / 10 ** d0;
    const x1 = Number(mayor(c.bruto1, v.pr1)) / 10 ** d1;
    const ganado = v.refEsToken0 ? x0 + (precio > 0 ? x1 / precio : 0) : x1 + x0 * precio;
    total += ganado;

    const dir = v.direccion.toLowerCase();
    const ap = aperturas.get(dir);
    const capital = ap ? Number(ap.capital) / 10 ** (v.refEsToken0 ? d0 : d1) : 0;
    const tsA = ap ? fechas.get(ap.block) : undefined;
    const cb = cierres.get(dir);
    const tsC = cb ? fechas.get(cb) : undefined;
    const duro = tsA !== undefined && tsC !== undefined && tsC > tsA ? tsC - tsA : undefined;
    lista.push({
      direccion: v.direccion, red: red.clave, par: `${s0 ?? "?"}/${s1 ?? "?"}`,
      ganado, capital, salio: capital + ganado,
      pct: capital > 0 ? (ganado / capital) * 100 : undefined,
      duro, simboloRef: v.refEsToken0 ? (s0 ?? "?") : (s1 ?? "?"),
    });
  }
  return { total, lista };
}

/**
 * LAS PISCINAS DE UNA RED. Una consulta por red, no una para las cuatro.
 *
 * ANTES ERA UNA SOLA CONSULTA para todo, y tenía los dos problemas que se ven desde fuera: la
 * portada no enseñaba nada hasta que respondía la red MÁS LENTA —BNB Chain, que tarda diez segundos
 * en servir sus eventos—, y una red caída se llevaba por delante el tiempo de las otras tres. Ahora
 * cada red pinta en cuanto llega.
 *
 * Y las lecturas van AGRUPADAS. Se leía vault por vault, con tres multicalls cada uno, incluidos los
 * CERRADOS: en Arbitrum eran once vaults leídos enteros para quedarse con dos. Aquí se pregunta
 * primero quién está abierto —un multicall para todos— y solo a esos se les lee lo demás, otra vez
 * en bloque. De ~40 idas y vueltas por red a 5.
 */
async function piscinasDe(
  red: RedKeel,
): Promise<{ vaults: VaultPublico[]; ganadoCerrado: number; cerradas: VaultCerrada[] }> {
  const client = clientePublico(red.idCadena);
  if (!client) throw new Error("sin cliente");

  // ---- 1. todas las direcciones, factory a factory
  const cuentas = await client.multicall({
    allowFailure: false,
    contracts: red.factories.map((f) => ({
      address: f, abi: keelVaultFactoryAbi, functionName: "vaultsCount" as const,
    })),
  });
  const indices = red.factories.flatMap((f, i) =>
    Array.from({ length: Number(cuentas[i] ?? 0n) }, (_, k) => ({ f, k }))
  );
  if (!indices.length) return { vaults: [], ganadoCerrado: 0, cerradas: [] };
  const direcciones = await client.multicall({
    allowFailure: false,
    contracts: indices.map(({ f, k }) => ({
      address: f, abi: keelVaultFactoryAbi, functionName: "allVaults" as const, args: [BigInt(k)],
    })),
  });

  // ---- 2. quién tiene posición abierta. Un vault cerrado y a cero no es flota: enseñarlo infla el
  //         número y le quita valor al que sí está trabajando.
  const ids = await client.multicall({
    allowFailure: true,
    contracts: direcciones.map((d) => ({ address: d, abi: keelVaultAbi, functionName: "tokenId" as const })),
  });
  const idList = direcciones
    .map((direccion, i) => ({ direccion, tokenId: ids[i]?.status === "success" ? (ids[i]!.result as bigint) : 0n }));
  const vivos = idList.filter((v) => v.tokenId !== 0n);
  const cerrados = idList.filter((v) => v.tokenId === 0n).map((v) => v.direccion);
  const todas = idList.map((v) => v.direccion);

  // Sin flota viva no hay piscinas que pintar, pero un vault cerrado pudo repartir mientras vivió y
  // eso sí cuenta en «Ganancias generadas»: se calcula aparte y se devuelve solo esa cifra.
  if (!vivos.length) {
    const hist = await cobradoPorVault(client, red, cerrados).catch(() => new Map<string, Cobrado>());
    const cc = await ganadoDeCerrados(client, clienteLogs(red), red, cerrados, hist);
    return { vaults: [], ganadoCerrado: cc.total, cerradas: cc.lista };
  }

  // ---- 3. lo que hace falta de cada uno, TODO en un multicall
  const CAMPOS = ["pool", "npm", "token0", "token1", "recoverToken0", "costBasisRef", "profit0", "profit1"] as const;
  const base = await client.multicall({
    allowFailure: true,
    contracts: vivos.flatMap((v) =>
      CAMPOS.map((functionName) => ({ address: v.direccion, abi: keelVaultAbi, functionName }))
    ),
  });
  /** El campo `c` del vault número `i`. `allowFailure` porque los clones anteriores al 09-ago no
   *  tienen `profit0/1`: ahí la cifra falta, no la fila. */
  const dato = <T,>(i: number, c: (typeof CAMPOS)[number]): T | undefined => {
    const r = base[i * CAMPOS.length + CAMPOS.indexOf(c)];
    return r?.status === "success" ? (r.result as T) : undefined;
  };

  const conBase = vivos.map((v, i) => ({
    ...v,
    pool: dato<Address>(i, "pool"),
    npm: dato<Address>(i, "npm"),
    t0: dato<Address>(i, "token0"),
    t1: dato<Address>(i, "token1"),
    refEsToken0: dato<boolean>(i, "recoverToken0") ?? true,
    costBasisRef: dato<bigint>(i, "costBasisRef") ?? 0n,
    pr0: dato<bigint>(i, "profit0"),
    pr1: dato<bigint>(i, "profit1"),
  })).filter((v) => v.pool && v.npm && v.t0 && v.t1);

  // ---- 4. tokens, precio y rango: otro bloque para todos a la vez
  const POR_VAULT = 6;
  const mercado = await client.multicall({
    allowFailure: true,
    contracts: conBase.flatMap((v) => [
      { address: v.t0!, abi: erc20Abi, functionName: "symbol" as const },
      { address: v.t0!, abi: erc20Abi, functionName: "decimals" as const },
      { address: v.t1!, abi: erc20Abi, functionName: "symbol" as const },
      { address: v.t1!, abi: erc20Abi, functionName: "decimals" as const },
      { address: v.pool!, abi: POOL_ABI, functionName: "slot0" as const },
      { address: v.npm!, abi: NPM_ABI, functionName: "positions" as const, args: [v.tokenId] },
    ]),
  });
  const campo = <T,>(i: number, j: number): T | undefined => {
    const r = mercado[i * POR_VAULT + j];
    return r?.status === "success" ? (r.result as T) : undefined;
  };

  // ---- 5. lo que sigue dentro del NFT. `collect` no es `view`, pero simulado desde el dueño del
  //         NFT dice justo lo que hay pendiente. Es la única lectura que no se puede agrupar.
  const pendientes = await Promise.all(
    conBase.map((v) =>
      client
        .simulateContract({
          address: v.npm!, abi: NPM_ABI, functionName: "collect",
          args: [{ tokenId: v.tokenId, recipient: v.direccion, amount0Max: MAX_U128, amount1Max: MAX_U128 }],
          account: v.direccion,
        })
        .then((r) => r.result as readonly [bigint, bigint])
        .catch(() => null)
    )
  );

  /** Lo que hace falta para pasar el histórico de cada piscina a su moneda de referencia. */
  const conversores = new Map<string, {
    d0: number; d1: number; refEsToken0: boolean; precio: number; pendiente: number;
    pr0?: bigint; pr1?: bigint; cbRaw: bigint;
  }>();
  const piscinas: VaultPublico[] = [];
  for (const [i, v] of conBase.entries()) {
    const s0 = campo<string>(i, 0), d0 = campo<number>(i, 1);
    const s1 = campo<string>(i, 2), d1 = campo<number>(i, 3);
    const slot0 = campo<readonly [bigint, number]>(i, 4);
    const pos = campo<readonly unknown[]>(i, 5);
    if (!s0 || !s1 || d0 === undefined || d1 === undefined || !slot0 || !pos) continue;

    const sp = Number(slot0[0]) / 2 ** 96;
    const precio = sp * sp * 10 ** (d0 - d1);
    const lo = Number(pos[5]), hi = Number(pos[6]), L = Number(pos[7]);
    const sa = 1.0001 ** (lo / 2), sb = 1.0001 ** (hi / 2);
    const spc = Math.min(Math.max(sp, sa), sb);
    const a0 = (spc < sb ? L * (1 / spc - 1 / sb) : 0) / 10 ** d0;
    const a1 = (spc > sa ? L * (spc - sa) : 0) / 10 ** d1;
    const aRef = (x0: number, x1: number) =>
      v.refEsToken0 ? x0 + (precio > 0 ? x1 / precio : 0) : x1 + x0 * precio;
    const cruda = (b0: bigint, b1: bigint) => aRef(Number(b0) / 10 ** d0, Number(b1) / 10 ** d1);

    const pend = pendientes[i];
    const sabeGanado = v.pr0 !== undefined || v.pr1 !== undefined || pend !== null;
    const pendiente = pend ? cruda(pend[0], pend[1]) : 0;
    const ganado = cruda(v.pr0 ?? 0n, v.pr1 ?? 0n) + pendiente;
    const capital = Number(v.costBasisRef) / 10 ** (v.refEsToken0 ? d0 : d1);

    piscinas.push({
      direccion: v.direccion, red: red.clave, pool: v.pool!, par: `${s0}/${s1}`, s0, s1,
      valor: aRef(a0, a1),
      capital,
      simboloRef: v.refEsToken0 ? s0 : s1,
      precio,
      lo: 1.0001 ** lo * 10 ** (d0 - d1),
      hi: 1.0001 ** hi * 10 ** (d0 - d1),
      enRango: slot0[1] >= lo && slot0[1] < hi,
      ganado: sabeGanado ? ganado : undefined,
      pctGanado: sabeGanado && capital > 0 ? (ganado / capital) * 100 : undefined,
    });

    // Se guardan aparte para el paso 6, que necesita convertir el histórico a la referencia.
    conversores.set(v.direccion.toLowerCase(),
      { d0, d1, refEsToken0: v.refEsToken0, precio, pendiente, pr0: v.pr0, pr1: v.pr1, cbRaw: v.costBasisRef ?? 0n });
  }

  // ---- 6. dos consultas de eventos para TODA la red, no una por vault
  const logs = clienteLogs(red);
  const cuando = logsConVentanas<{ address: string; blockNumber: bigint; args: { costBasisRef?: bigint } }>(
    logs, { address: piscinas.map((p) => p.direccion), event: OPENED }, red, { acumula: true }
  ).then((ls) => {
    // El ÚLTIMO `Opened` de cada vault: una piscina vaciada y reabierta lleva viva desde la apertura
    // de ahora, no desde que nació el contrato. Se guarda su bloque (para la antigüedad) Y su
    // `costBasisRef` (el capital con el que ABRIÓ, denominador honesto del %).
    const ultimo = new Map<string, { block: bigint; cbRef: bigint }>();
    for (const l of ls) {
      const k = l.address.toLowerCase();
      const blk = l.blockNumber ?? 0n;
      if (blk > (ultimo.get(k)?.block ?? -1n)) ultimo.set(k, { block: blk, cbRef: l.args?.costBasisRef ?? 0n });
    }
    return ultimo;
  }).catch(() => new Map<string, { block: bigint; cbRef: bigint }>());

  // Para TODAS —vivas y cerradas— en una sola consulta de eventos: las vivas lo usan para no perder
  // lo ya cobrado, y para las cerradas es lo único que las cuenta. Va en paralelo con `cuando`.
  const cobrado = cobradoPorVault(client, red, todas).catch(() => new Map<string, Cobrado>());

  const [ultimo, historico] = await Promise.all([cuando, cobrado]);

  const fechas = new Map<bigint, number>();
  await Promise.all(
    [...new Set([...ultimo.values()].map((v) => v.block))].map((b) =>
      client.getBlock({ blockNumber: b }).then((x) => fechas.set(b, Number(x.timestamp))).catch(() => undefined)
    )
  );

  for (const p of piscinas) {
    const k = p.direccion.toLowerCase();
    const ap = ultimo.get(k);
    p.abierta = ap ? fechas.get(ap.block) : undefined;

    // CAPITAL HONESTO PARA EL %. El `costBasisRef` en VIVO baja cuando el dueño retira principal
    // (`costBasisRef -= salida`), así que dividir el ganado de TODA LA VIDA por el capital que queda
    // hoy dispara el porcentaje —visto +260,9% con 4,51 ganado sobre 1,73 restante en un vault que
    // abrió con 195—. Se toma el MAYOR entre el vivo y el de apertura (evento `Opened`): una piscina
    // que reinvirtió y creció usa el valor grande de hoy; una de la que se retiró, el de apertura.
    const conv = conversores.get(k);
    if (conv && ap && ap.cbRef > (conv.cbRaw ?? 0n)) {
      p.capital = Number(ap.cbRef) / 10 ** (conv.refEsToken0 ? conv.d0 : conv.d1);
      if (p.ganado !== undefined) p.pctGanado = p.capital > 0 ? (p.ganado / p.capital) * 100 : undefined;
    }

    // LO GANADO NO SE BORRA AL COBRARLO. Hasta aquí `ganado` es lo apartado ahora más lo pendiente,
    // y `withdrawFees()` vacía lo primero: una piscina que ha repartido bien durante meses aparecía
    // con un «+0,02». El histórico ya incluye lo apartado, por eso lo sustituye en vez de sumarse.
    const c = historico.get(k);
    if (!c || !conv || p.ganado === undefined) continue;
    // EL MAYOR DE LOS DOS, TOKEN A TOKEN. Los dos son cotas por debajo: lo apartado se vacía al
    // retirar, y el histórico se pierde cuando la comisión del 10% REDONDEA A CERO —pasa en tokens
    // de 8 decimales con cantidades diminutas, visto en `0x02EDdE73` con `fee0 = 0` y 14 unidades
    // de WBTC apartadas—. La misma cuenta que hace la lista privada, para que no discrepen.
    const mayor = (a: bigint, b: bigint | undefined) => (a > (b ?? 0n) ? a : b ?? 0n);
    const x0 = Number(mayor(c.bruto0, conv.pr0)) / 10 ** conv.d0;
    const x1 = Number(mayor(c.bruto1, conv.pr1)) / 10 ** conv.d1;
    p.ganado = (conv.refEsToken0
      ? x0 + (conv.precio > 0 ? x1 / conv.precio : 0)
      : x1 + x0 * conv.precio) + conv.pendiente;
    p.pctGanado = p.capital > 0 ? (p.ganado / p.capital) * 100 : undefined;
  }

  // Las cerradas reusan el mismo histórico y el mismo cliente de logs.
  const cc = await ganadoDeCerrados(client, logs, red, cerrados, historico);
  return { vaults: piscinas, ganadoCerrado: cc.total, cerradas: cc.lista };
}

/**
 * Lo que hay funcionando AHORA, en las cuatro redes. Es la pagina publica: no pide wallet y no
 * depende de la red a la que estes conectado.
 *
 * Cada red va por su cuenta y se combinan aqui: la portada enseña lo que ya ha llegado en vez de
 * esperar a la ultima. Si una red no responde se dice CUAL, en vez de sumar de menos y presentarlo
 * como si fuera el total — un dato incompleto que se presenta como completo es peor que no tenerlo.
 */
export function useFlota() {
  const redes = redesActivas();

  return useQueries({
    queries: redes.map((red) => ({
      queryKey: ["flota", red.clave],
      // 30 s, no 120. La portada es lo primero que mira quien acaba de crear una piscina, y con dos
      // minutos de caché su piscina tardaba en salir aunque ya estuviera en cadena. Al crear se
      // invalida (ver `useCrear`); esto cubre además el caso de mirar la portada desde otra pestaña.
      staleTime: 30_000,
      queryFn: () => piscinasDe(red),
      // Si un refresco falla (el RPC de Robinhood a veces devuelve 429), se conserva el último dato
      // bueno en vez de enseñar 0 piscinas: un 0 falso es peor que un dato de hace un minuto.
      placeholderData: (previo: Awaited<ReturnType<typeof piscinasDe>> | undefined) => previo,
    })),
    combine: (resultados) => {
      const vaults = resultados.flatMap((r) => r.data?.vaults ?? []).sort((a, b) => b.valor - a.valor);
      const ganadoCerradoPorRed: Partial<Record<RedKeel["clave"], number>> = {};
      redes.forEach((red, i) => {
        const g = resultados[i]?.data?.ganadoCerrado;
        if (g) ganadoCerradoPorRed[red.clave] = g;
      });
      const data: Flota = {
        vaults,
        total: vaults.reduce((s, v) => s + v.valor, 0),
        enRango: vaults.filter((v) => v.enRango).length,
        redesCaidas: redes.filter((_, i) => resultados[i]?.isError).map((r) => r.etiqueta),
        ganadoCerrado: resultados.reduce((s, r) => s + (r.data?.ganadoCerrado ?? 0), 0),
        ganadoCerradoPorRed,
        cerradas: resultados.flatMap((r) => r.data?.cerradas ?? []).sort((a, b) => b.ganado - a.ganado),
      };
      return {
        // Hay dato en cuanto responde la PRIMERA red: lo demás va apareciendo. El esqueleto solo
        // tiene sentido mientras no hay nada que enseñar.
        data: resultados.some((r) => r.data) ? data : undefined,
        isPending: resultados.every((r) => r.isPending),
        isCargando: resultados.some((r) => r.isPending),
        /** cuándo se leyó la cadena por última vez (la red más reciente), para el «actualizado hace…» */
        dataUpdatedAt: Math.max(0, ...resultados.map((r) => r.dataUpdatedAt ?? 0)),
        /** redes sin ningún dato todavía (cargando) o que fallaron sin dato previo: su contador no es fiable */
        redesSinDato: redes.filter((_, i) => !resultados[i]?.data).map((r) => r.clave),
      };
    },
  });
}
